/* Seamless marquee looping.

   A marquee renders its items twice and slides the pair left by one set. That
   only reads as endless while a single set is wider than the strip — with few
   or short items the track runs out and a blank gap scrolls past before the
   loop restarts. This element measures one set, clones it until the track
   covers the viewport plus one set of travel, and hands the exact step width
   back to CSS as --marquee-shift so the restart lands invisibly. */

class MarqueeLoop extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-marquee-track]');
    if (!this.track || this.sets.length < 2) return;

    this.update = this.update.bind(this);
    this.update();

    // Web fonts and lazy images land after first paint and resize the set.
    if (document.fonts) document.fonts.ready.then(this.update);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.sets[0]);
    } else {
      window.addEventListener('resize', this.update);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener('resize', this.update);
  }

  get sets() {
    return Array.from(this.track.children);
  }

  update() {
    const sets = this.sets;

    // Nothing to size where the marquee isn't running: reduced motion, or a
    // breakpoint that lays the items out as a static wrapping row.
    if (getComputedStyle(this.track).animationName === 'none') return;

    // Distance between two neighbouring sets — the item widths plus whichever
    // gap the section uses. A shared translate on the track cancels out.
    const step = sets[1].getBoundingClientRect().left - sets[0].getBoundingClientRect().left;
    if (step <= 0) return;

    this.style.setProperty('--marquee-shift', `${step}px`);

    // One set leaves the viewport per cycle, so the track has to span the
    // viewport plus that set's worth of travel.
    const needed = Math.max(2, Math.ceil(this.clientWidth / step) + 1);

    for (let i = sets.length; i < needed; i++) this.track.appendChild(this.buildClone(sets[0]));
    for (let i = sets.length; i > needed; i--) this.track.lastElementChild.remove();
  }

  // Clones are decoration: hidden from assistive tech, skipped by the keyboard,
  // and stripped of theme editor block ids so a block isn't targeted twice.
  buildClone(set) {
    const clone = set.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-marquee-clone', '');

    clone.querySelectorAll('a, button, input, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });

    [clone, ...clone.querySelectorAll('*')].forEach((element) => {
      Array.from(element.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('data-shopify')) element.removeAttribute(attribute.name);
      });
    });

    return clone;
  }
}

if (!customElements.get('marquee-loop')) {
  customElements.define('marquee-loop', MarqueeLoop);
}
